<template>
  <v-expansion-panels>
    <v-expansion-panel
      class="payment-details pri-center-expansion-panel"
      role="presentation"
      value="openPanel"
    >
      <!-- panel title -->
      <v-expansion-panel-title>
        <template #default>
          <v-row justify="center">
            <v-col class="text-center text-uppercase" justify="center">
              {{ $t('L_policy_view_policy_details') }}
            </v-col>
          </v-row>
        </template>
      </v-expansion-panel-title>
      <!-- panel body -->
      <v-expansion-panel-text role="presentation" class="bg-grey-lighten-4 px-0 mx-0">
        <v-row class="px-0 mx-0">
          <v-col cols="12" class="px-0 mx-0">
            <v-tabs id="coverage_policy_tabs" v-model="tabs" grow>
              <v-tab ripple>
                <v-icon
                  v-if="!isMobile"
                  color="primary"
                  class="mr-2"
                  size="x-large"
                  :icon="mdiAccountMultipleOutline"
                />
                <div class="inherit cobalt text-capitalize icon-text-tab">
                  {{ __t('L_policy_coverage_details') }}
                </div>
              </v-tab>
              <v-tab ripple>
                <v-icon
                  v-if="!isMobile"
                  color="primary"
                  class="mr-2"
                  size="x-large"
                  :icon="mdiFileOutline"
                />
                <div class="inherit cobalt text-capitalize icon-text-tab">
                  <a @click.stop="navigateToDocumentsPage" v-if="!isMobile">{{
                    $t('L_policy_policy_documents')
                  }}</a>
                  <a v-else @click.stop="navigateToDocumentsPage"
                    >{{ $t('L_term_life') }} <br />
                    {{ $t('L_policy_policy_documents_third') }}</a
                  >
                </div>
              </v-tab>
            </v-tabs>
            <v-tabs-window v-model="tabs">
              <v-tabs-window-item>
                <v-container fluid class="px-0">
                  <!-- for mobile -->
                  <div v-if="isMobile">
                    <v-card
                      v-for="(item, index) in formattedCoverageData"
                      :key="index + 'insured'"
                      :color="index % 2 === 0 ? '#fafcfd' : 'white'"
                      flat
                      density="compact"
                      rounded="0"
                    >
                      <v-card-text class="text-body-1 px-0">
                        <v-col cols="12" sm="8" offset-sm2>
                          <v-row
                            row
                            wrap
                            v-for="(header, headerIndex) in formattedHeaders"
                            :key="headerIndex + 'insured'"
                            class="data-row"
                          >
                            <v-col cols="6" class="font-weight-medium">
                              <span>{{ header.title }}</span>
                            </v-col>
                            <v-col cols="6">
                              <span>{{ item ? item[header.value] : undefined }}</span>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-card-text>
                      <v-divider />
                    </v-card>
                  </div>
                  <!-- for web -->
                  <v-data-table
                    v-else
                    :aria-label="$t('L_policy_coverage_details')"
                    :headers="formattedHeaders"
                    :items="formattedCoverageData"
                    hide-default-footer
                    class="elevation-0 bg-white"
                  >
                  </v-data-table>
                </v-container>
              </v-tabs-window-item>
            </v-tabs-window>
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { currency, formatDate } from '@/shared/util/formatters';
import stringValues from '@/shared/util/stringValues';
import DynatraceClient from '@/plugins/dynatracePlugin';
import { mapState, mapStores } from 'pinia';
import { usePolicyStore } from '@/modules/policy/store/policy.store';

import coverageDetailsIcon from './assets/coverage-details-icon.png';
import disabledPolicyDocIcon from './assets/disabled-policy-doc-icon.png';
import { mdiAccountMultipleOutline, mdiFileOutline } from '@mdi/js';
import { RouteNames } from '@/router/routeNames';
import type { IPolicyStorePolicyPhases } from '@/modules/policy/store/policyStore.model.ts';

const componentOptions: ComponentOptions = {
  name: 'PolicyDetailsPanel',
  data: () => ({
    coverageDetailsIcon,
    disabledPolicyDocIcon,
    mobile: false,
    headers: [
      {
        title: 'L_policy_insured',
        value: 'insuredName',
      },
      {
        title: 'L_policy_dob',
        value: 'dateOfBirth',
      },
      {
        title: 'L_policy_sex',
        value: 'sex',
      },
      {
        title: 'L_policy_age',
        value: 'issueAge',
      },
      {
        title: 'L_policy_plan_type',
        value: 'planDescription',
      },
      {
        title: 'L_policy_underwriting_class',
        value: 'underwritingClass',
      },
      {
        title: 'L_policy_issue_date',
        value: 'issueDate',
      },
      {
        title: 'L_policy_benefit_amount',
        value: 'benefitAmount',
      },
    ],
    openPanel: [],
    tabs: 0,
    mdiAccountMultipleOutline,
    mdiFileOutline,
  }),
  props: {
    policyData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapStores(usePolicyStore),
    ...mapState(usePolicyStore, {
      policy: 'currentPolicy',
      step: 'step',
    }),
    isUsPolicy() {
      return this?.policy?.contractId.slice(0, 2) === '04';
    },
    isNyPolicy() {
      return this?.policy?.isNewYorkPolicy;
    },
    isCanadaPolicy() {
      return this?.policy?.isCanadaPolicy;
    },
    headerTransformer() {
      const transformer = (text: string) => {
        const translateHeaders: Record<string, boolean> = {
          L_policy_plan_type: true,
          L_policy_underwriting_class: true,
          L_policy_benefit_amount: true,
        };
        return translateHeaders[text] ? this.__t.bind(this)(text) : this.$t(text);
      };
      return transformer.bind(this);
    },
    formattedCoverageData() {
      const filteredPhases = this.policyData.phases.filter((ph: IPolicyStorePolicyPhases) => {
        return ph.isAnnuity === false;
      });
      const mappedPhases = filteredPhases.map((phase: IPolicyStorePolicyPhases) => {
        let underwritingClass;
        if (phase.underwritingClass && stringValues.underwritingClass[phase.underwritingClass]) {
          underwritingClass = this.$t(stringValues.underwritingClass[phase.underwritingClass]);
        } else {
          underwritingClass = phase.underwritingClass || this.$t('L_na');
        }
        const r = {
          insuredName: phase.insuredName,
          dateOfBirth: formatDate(phase.dateOfBirth?.toISOString() ?? '') || phase.dateOfBirth,
          issueAge: phase.issueAge || this.$t('L_na'),
          underwritingClass: underwritingClass,
          issueDate: formatDate(phase.issueDate?.toISOString() ?? '') || phase.issueDate,
          benefitAmount: currency(Math.round(+phase.coverageAmount), 0),
          planDescription: phase.planDescription,
          planText: phase.planText,
          sex: phase.sex,
        };
        return r;
      });
      return mappedPhases;
    },
    formattedHeaders() {
      const headersMapper = (value: { title: string; value: string; sortable?: boolean }) => {
        value.sortable = false;
        // value.class = `pl-2 column-header${this.policyData.pending ? '' : ' pt-2'}`;
        value.title = this.$t(value.title);
        return value;
      };
      return this.headers.map((d: { title: string; value: string; sortable?: boolean }) =>
        headersMapper(d)
      );
    },
    isMobile() {
      return this.policy && this.$vuetify.display.xs;
    },
  },
  methods: {
    __t(key: string): string {
      return this.$t(key + (this.policyStore.isPending ? '_application' : ''));
    },
    navigateToDocumentsPage() {
      try {
        const i = DynatraceClient.enterAction('link_policyDocuments_policyDetailsPage');
        DynatraceClient.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      if (this.isMobile) {
        this.$router.push({
          name: RouteNames.DOCUMENT_LIST_MOBILE,
          params: { routeName: RouteNames.POLICY_DOCUMENTS },
        });
      } else {
        this.$router.push({ name: RouteNames.POLICY_DOCUMENTS }); // default
      }
    },
  },
  watch: {
    openPanel() {
      // Have to do this because vuetify tabs will not render
      // the slider as selected while the expansion panel is closed
      const slider = this.$el.querySelector('.v-tabs__slider-wrapper');
      if (slider) {
        const numTabs = this.policyData.pending ? 1 : 2;
        const sliderWidth = `${100 / numTabs}%`;
        // Only update if width is not set or the width is 0px
        if (!slider.style.width || slider.style.width === '0px') {
          slider.style.width = sliderWidth;
        }
      }
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.cobalt {
  color: #02579e;
}
.textUppercase {
  text-transform: uppercase;
}
</style>
